<template>
    <div class="relative">
        <div class="sticky top-0 z-50 bg-white w-full min-h-[62px]">
            <placement-header-alt
                :placement="mainPlacement"
                :placementData="currentGroupActual"
                :index="0"
                :has-error="localErrorState"
            >
                <div v-if="localErrorState"
                    class="text-red-error text-sm absolute top-[34px]"
                >
                    {{ $t('SELECT_STYLE_NO_PAINTFILL') }}
                </div>
            </placement-header-alt>
        </div>
        <div class="mt-[29.73px]"></div>
        <step-options-initials-buttons
            v-for="(option, index) in styleOptions"
            :key="option.code"
            background-size="auto 100%"
            :class="[
                'px-[20px] lg:px-[40px]',
                {
                    'mt-[24px]': index > 0,
                }
            ]"

            :main-placement="mainPlacement"
            :option="option"
            @unselect="selectNoThanks"
            @select="selectActual"
            :has-error-state="localErrorState"
        >
            <transition name="cz3__fade">
                <div v-if="!selectedPlaceholder && index === 0"
                    class="mt-[24px]"
                >
                    <initials-colors
                        v-if="hasLoft"
                        :placement="mainPlacement"
                        :colors-placement="'StampingColorsLoft'"
                    />

                    <initials-colors
                        v-if="hasBounce"
                        :placement="mainPlacement"
                        :colors-placement="'StampingColorsBounce'"
                    />

                    <initials-colors
                        v-if="hasGrind"
                        :placement="mainPlacement"
                        :colors-placement="'StampingColorsGrind'"
                    />

                    <initials-colors
                        v-if="hasWing"
                        :placement="mainPlacement"
                        :colors-placement="'StampingColorsWing'"
                    />
                </div>
            </transition>
        </step-options-initials-buttons>

        <!-- <div v-for="option in styleOptions"
            :key="option.value"
            class="mt-4"
        >
            <base-checkbox
                :checkbox-id="option.value"
                :value="option.value === selectedValue"
                @change="updatePaintfill(option)"
            >
                {{option.label}} <span>{{formatPrice(option)}}</span>
            </base-checkbox>
        </div> -->
    </div>
</template>

<script>
    import InitialsColors from './InitialsColors.vue';
    import StepOptionsInitialsButtons from './buttons/StepOptionsInitialsButtons.vue';
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';

    import allStores from '../stores/all';

    export default {
        mixins: [allStores],

        components: {
            InitialsColors,
            StepOptionsInitialsButtons,
            PlacementHeaderAlt,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                paintfillEnabled: false,
                localErrorState: false,
            };
        },

        computed: {
            isCompleteSelection() {
                return true;
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            currentGroupActual() {
                return this.visibleGroups.find((group) => group.name === this.currentGroupName);
            },

            currentGroupName() {
                return this.customizerStore.currentGroup;
            },

            visiblePlacements() {
                const placements = this.customizerStore.visiblePlacements(this.itemData) ?? [];

                return placements.map((placement) => {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    // Add extra properties to each placement:
                    // - a code of the currently selected component.
                    return {
                        selectedValue: selected ? selected.code : null,
                        selectedPlaceholder: selected ? selected.placeholder : null,
                        groupType: placement.custom?.['v-group-type'] ?? 'standard-buttons',
                        ...placement,
                    };
                });
            },

            mainPlacement() {
                return this.visiblePlacements.find((x) => x.code === 'Paintfill');
            },

            selectedValue() {
                return this.mainPlacement.selectedValue;
            },

            selectedPlaceholder() {
                return this.mainPlacement.selectedPlaceholder;
            },

            styleOptions() {
                return this.customizerStore.sortOptions(this.mainPlacement, this.selectedValue);
            },

            hasLoft() {
                const placement = this.viewController.findPlacement('StampingColorsLoft');

                if (placement?.custom?.['v-allow-color'] === 'no') {
                    return false;
                }

                return true;
            },

            hasBounce() {
                const placement = this.viewController.findPlacement('StampingColorsBounce');

                if (placement?.custom?.['v-allow-color'] === 'no') {
                    return false;
                }

                return true;
            },

            hasGrind() {
                const placement = this.viewController.findPlacement('StampingColorsGrind');

                if (placement?.custom?.['v-allow-color'] === 'no') {
                    return false;
                }

                return true;
            },

            hasWing() {
                const placement = this.viewController.findPlacement('StampingColorsWing');

                if (placement?.custom?.['v-allow-color'] === 'no') {
                    return false;
                }

                return true;
            },

            panelToReset() {
                return this.customizerStore.panelToReset;
            },
        },

        watch: {
            selectedPlaceholder(newValue, oldValue) {
                if (!oldValue && newValue) {
                    // Reset defaults.
                    this.customizerStore.pickDefaultColors(this.viewController);
                }
            },
            panelToReset(val) {
                if (val === 'Paintfill') {
                    this.selectNoThanks();
                }
            },
        },

        methods: {
            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            handleApplySelections() {
                this.setShowGroupOverlay(false);
                this.$emit('applySelections');
            },

            updatePaintfill(option) {
                this.viewController.updateComponent(this.mainPlacement.code, option.value);
            },

            formatPrice(option) {
                return this.customizerStore.priceFormatted(
                    this.customizerStore.getPrice(this.viewController, option.raw),
                    true,
                );
            },

            selectNoThanks() {
                const placement = this.mainPlacement;

                if (!placement) {
                    return;
                }

                const placeholder = placement.components.find(
                    (x) => x.placeholder
                        && this.viewController.isAvailable(x)
                        && x.placeholderOptional,
                );

                if (!placeholder) {
                    return;
                }

                this.viewController.updateComponent(placement.code, placeholder.code);
            },

            selectActual({ placement, option }) {
                this.viewController.updateComponent(placement, option);
            },
        },
    };
</script>
